import ChatStrategy from './ChatStrategy';

export default class RATRepairScopeStrategy extends ChatStrategy {
  constructor() {
    super();
    this.retryMessages = ['Updating repair recommendations...'];
    this.type = 'repair_scope';
  }

  getIdentity(metadata) {
    return metadata?.ratRow?.id?.toString() ?? 'unknown';
  }

  getStrategyTitle() {
    return 'Repair Select - Repair Scope';
  }

  getStrategyDescription(metadata) {
    const { ratRow } = metadata;
    const description = ratRow?.partName;
    return description ? `${description}` : '';
  }

  createPayload(chatId, metadata, interactions) {
    const sanitize = (v) => (typeof v === 'string' ? v.replace(/'/g, '') : v);

    const {
      serialNumber,
      model,
      originalOEMNumber,
      customerInfoText,
      ratRow,
      workOrderName,
    } = metadata;

    if (!ratRow || typeof ratRow !== 'object') {
      throw new Error(
        'RATRepairScopeStrategy requires a single ratRow in currentJob'
      );
    }

    const ratData = [
      {
        assembly: sanitize(ratRow.assembly || ''),
        clientReject: ratRow.clientReject || false,
        dim: sanitize(ratRow.dim || ''),
        estimatedLabor: sanitize(ratRow.estimatedLabor || ''),
        id: sanitize(ratRow.id || ''),
        itemNumber: isNaN(Number(ratRow.itemNumber))
          ? 0
          : Number(ratRow.itemNumber),
        level: ratRow.level || 0,
        missing: ratRow.missing || 0,
        ndt: sanitize(ratRow.ndt || ''),
        partName: sanitize(ratRow.partName || ''),
        partNumber: sanitize(ratRow.partNumber || ''),
        quantity: ratRow.quantity || 0,
        received: ratRow.received || 0,
        repair: ratRow.repair || 0,
        repairScope: sanitize(ratRow.repairScope || ''),
        replace: ratRow.replace || 0,
        reuse: ratRow.reuse || 0,
        reviewed: ratRow.reviewed || false,
        summary: sanitize(ratRow.summary || ''),
        visual: sanitize(ratRow.visual || ''),
        componentType: ratRow.isMinor ? 'minor' : 'major',
      },
    ];

    return {
      providedId: chatId,
      requestType: 'repair_scope',
      serialNumber,
      model: model.map((m) => sanitize(m.text)),
      oem: [sanitize(originalOEMNumber)],
      productLine: workOrderName.map((productLine) =>
        sanitize(productLine.text)
      ),
      customer: sanitize(customerInfoText),
      ratData,
      interactionData: interactions,
    };
  }

  parseUserMessage() {
    const idx = Math.floor(Math.random() * this.retryMessages.length);
    return this.retryMessages[idx];
  }

  onMessageReceived(event) {
    if (event.status_code !== 200) {
      return {
        text: 'An error has occurred while generating the RAT repair scope.',
        isError: true,
      };
    }

    let documents = [];
    let response = '';
    try {
      const parsed = JSON.parse(event.result);
      documents = parsed.documents;
      response = parsed.response;
    } catch (error) {
      response = event.result;
    }

    const normalizedDocuments = Array.isArray(documents)
      ? documents
          .map((doc) => {
            if (typeof doc === 'string') {
              return { title: doc, url: '' };
            }

            const title = doc?.Title || doc?.title || 'Untitled document';
            const url =
              doc?.presigned_url || doc?.presignedUrl || doc?.url || '';
            return { title, url };
          })
          .filter((doc) => doc.title)
      : [];

    const documentsSection = normalizedDocuments.length
      ? `\n\n**Documents:**\n${normalizedDocuments
          .map((doc) =>
            doc.url ? `- [${doc.title}](${doc.url})` : `- ${doc.title}`
          )
          .join('\n')}`
      : '';

    return {
      text: `${response || ''}${documentsSection}`,
      // Keep documents empty so ChatMessage does not render a second bubble.
      documents: [],
      isError: false,
    };
  }
}
